import { useEffect } from 'react';
import type { VoiceSession } from './voice/VoiceSession';

export type KeyAction = 'whisper' | 'announce' | 'mute';

export interface KeyLike {
  code: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

/** Keyboard shortcuts: Space = whisper (players), Shift+Space = announce (quizmaster), M = mute. */
export function actionFor(e: KeyLike, isHost: boolean): KeyAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.code === 'KeyM' && !e.shiftKey) return 'mute';
  if (e.code !== 'Space') return null;
  if (isHost) return e.shiftKey ? 'announce' : null;
  return e.shiftKey ? null : 'whisper';
}

/** True when keys should go to a text field instead of triggering shortcuts. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'color', 'file'].includes(type);
  }
  return false;
}

export const SHORTCUT_LABEL = { whisper: 'Space', announce: 'Shift + Space', mute: 'M' };

/** Hold-to-talk bindings. Releasing the key (or leaving the window) always ends the hold. */
export function useVoiceShortcuts(session: VoiceSession | null, isHost: boolean) {
  useEffect(() => {
    if (!session) return;
    let held: 'whisper' | 'announce' | null = null;

    const release = () => {
      if (held === 'whisper') session.setWhisperHeld(false);
      if (held === 'announce') session.setAnnounceHeld(false);
      held = null;
    };

    const onDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const action = actionFor(e, isHost);
      if (!action) return;
      e.preventDefault(); // stop page scroll and stop Space from "clicking" a focused button
      if (e.repeat) return;
      if (action === 'mute') return session.toggleMute();
      held = action;
      if (action === 'whisper') session.setWhisperHeld(true);
      else session.setAnnounceHeld(true);
    };

    const onUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && held) {
        e.preventDefault();
        release();
      } else if (e.code === 'Space' && !isTypingTarget(e.target)) {
        e.preventDefault();
      }
    };

    const onHide = () => {
      if (document.visibilityState === 'hidden') release();
    };

    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      release();
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', onUp, true);
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [session, isHost]);
}
